import React, {
  useState,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Button, message } from "antd";
import { AudioOutlined, PauseOutlined } from "@ant-design/icons";

const AiVoiceRecorder = forwardRef(
  (
    {
      recordedFile,
      setRecordedFile,
      startRecord,
      setStartRecord,
      onRecordSubmit,
      isFloating = false,
    },
    ref
  ) => {
    const [isRecording, setIsRecording] = useState(false);
    const [recordingTime, setRecordingTime] = useState(0);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const timerRef = useRef(null);
    const streamRef = useRef(null); // Holds the MediaStream object
    const permissionRequestPendingRef = useRef(false);
    const micPermissionDeniedRef = useRef(false);
    const permissionToastShownRef = useRef(false);

    // 🔥 Expose cleanup method to parent via ref
    useImperativeHandle(ref, () => ({
      stopRecordingAndCleanup: () => {
        handleStopRecording();
        stopAllTracks(true); // Force stop all tracks and clear reference
      },
    }));

    // Start recording when startRecord prop changes
    useEffect(() => {
      if (startRecord && !isRecording) {
        handleStartRecording(true);
        setStartRecord(false);
      }
    }, [startRecord]);

    // Cleanup on unmount
    useEffect(() => {
      return () => {
        stopAllTracks(true); // Force stop and clear on unmount
        if (timerRef.current) {
          clearInterval(timerRef.current);
        }
      };
    }, []);

    // Function to stop all media tracks
    // Added a forceClear flag for unmount/ref cleanup
    const stopAllTracks = (forceClear = false) => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => {
          if (track.readyState === "live") {
            track.stop(); // Stops the mic/camera hardware access
          }
          console.log("✅ Track stopped:", track.kind, track.readyState);
        });

        // Only nullify the stream reference if forced (like on unmount)
        // or if not currently stopping via user click (handleStopRecording)
        if (forceClear) {
          streamRef.current = null;
        }
      }
    };

    const handleStartRecording = async (isAutoStart = false) => {
      if (permissionRequestPendingRef.current || isRecording) return;

      if (isAutoStart && micPermissionDeniedRef.current) {
        setStartRecord(false);
        return;
      }

      if (!isAutoStart) {
        micPermissionDeniedRef.current = false;
        permissionToastShownRef.current = false;
      }

      permissionRequestPendingRef.current = true;

      try {
        // Stop any existing stream first
        stopAllTracks(true); // Clear old stream reference completely

        // Request microphone access
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        streamRef.current = stream;
        micPermissionDeniedRef.current = false;
        permissionToastShownRef.current = false;

        // Create MediaRecorder
        const mediaRecorder = new MediaRecorder(stream);
        mediaRecorderRef.current = mediaRecorder;
        audioChunksRef.current = [];

        // Collect audio data
        mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            audioChunksRef.current.push(event.data);
          }
        };

        // Handle recording stop event (fired after mediaRecorder.stop())
        mediaRecorder.onstop = () => {
          const audioBlob = new Blob(audioChunksRef.current, {
            type: "audio/webm",
          });
          const audioFile = new File([audioBlob], "recording.webm", {
            type: "audio/webm",
          });
          setRecordedFile(audioFile);

          // 🟢 CRITICAL FINAL CLEANUP
          // Nullify the stream reference *after* the recording blob is finalized.
          // This is often the final necessary step for the browser to release the icon.
          streamRef.current = null;
          audioChunksRef.current = [];
        };

        // Start recording
        mediaRecorder.start();
        setIsRecording(true);
        setRecordingTime(0);

        // Start timer
        timerRef.current = setInterval(() => {
          setRecordingTime((prev) => prev + 1);
        }, 1000);

        // message.success("Recording started");
      } catch (error) {
        console.error("Error accessing microphone:", error);
        micPermissionDeniedRef.current = true;
        if (!permissionToastShownRef.current) {
          message.error("Failed to access microphone or permissions denied.");
          permissionToastShownRef.current = true;
        }
        setStartRecord(false);
        stopAllTracks(true); // Ensure cleanup if initial stream setup fails
      } finally {
        permissionRequestPendingRef.current = false;
      }
    };

    const handleStopRecording = () => {
      if (mediaRecorderRef.current && isRecording) {
        // 1. Clear timer
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }

        // 2. CRITICAL: Stop all tracks immediately (Releases hardware access)
        // This sends the signal to the browser to stop using the microphone.
        stopAllTracks();

        // 3. Stop the media recorder (triggers onstop for file creation)
        if (mediaRecorderRef.current.state !== "inactive") {
          mediaRecorderRef.current.stop();
        }

        // 4. Reset component state and references
        setIsRecording(false);
        // Nullify recorder ref. streamRef will be nullified in the asynchronous onstop.
        mediaRecorderRef.current = null;

        // message.success("Recording stopped");
      }
    };

    const formatTime = (seconds) => {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins.toString().padStart(2, "0")}:${secs
        .toString()
        .padStart(2, "0")}`;
    };

    // ... (rest of the return statement remains the same)
    return (
      <div
        className={
          isFloating
            ? "ai-voice-recorder ai-voice-recorder--floating"
            : "ai-voice-recorder"
        }
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: isFloating ? "10px" : "4px",
        }}
      >
        {/* Recording Indicator — hide wide timer in floating capsule */}
        {isRecording && !isFloating && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              color: "#ff4d4f",
            }}
          >
            <div
              style={{
                width: "6px",
                height: "6px",
                borderRadius: "50%",
                backgroundColor: "#ff4d4f",
                animation: "pulse 1.5s infinite",
              }}
            />
            <span style={{ fontSize: "14px", fontWeight: 500 }}>
              REC {formatTime(recordingTime)}
            </span>
          </div>
        )}

        {/* Record/Stop Button */}
        {!isFloating && (
          <div
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              backgroundColor: isRecording ? "#ff4d4f" : "#1890ff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              transition: "all 0.3s",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
            }}
            onClick={
              isRecording
                ? handleStopRecording
                : () => handleStartRecording(false)
            }
          >
            {isRecording ? (
              <PauseOutlined style={{ fontSize: "32px", color: "#fff" }} />
            ) : (
              <AudioOutlined style={{ fontSize: "32px", color: "#fff" }} />
            )}
          </div>
        )}

        {/* For floating mode, show compact controls */}
        {isFloating && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "10px",
              minHeight: isRecording ? 36 : 0,
            }}
          >
            {isRecording && (
              <div
                style={{
                  width: "36px",
                  height: "36px",
                  flex: "0 0 auto",
                  borderRadius: "50%",
                  backgroundColor: "#ff4d4f",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                }}
                onClick={handleStopRecording}
                aria-label="Stop recording"
              >
                <PauseOutlined style={{ fontSize: "20px", color: "#fff" }} />
              </div>
            )}
          </div>
        )}

        {/* CSS for pulse animation */}
        <style>{`
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.3;
            }
          }
        `}</style>
      </div>
    );
  }
);

AiVoiceRecorder.displayName = "AiVoiceRecorder";

export default AiVoiceRecorder;
